import cityDate from '@/assets/js/city'
import { Toast } from 'antd-mobile'
import { useCallback, useRef } from 'react'
import useCityLocation from './use-city-location'

interface Options {

  /** 渠道编码，用于地理定位逆编码上报。 */
  adChannelCode?: string

  /** 城市名称，为空时发起首次定位。 */
  cityName?: string

  /** 城市行政区划代码数组。 */
  cityCodes?: (string | null | undefined)[]

  /** 设置城市名称与代码的回调。 */
  setCityName?: (name: string) => void
  setCityCodes?: (codes: (string | null)[]) => void

  /** 旧页面的返回动作，保留既有浏览历史语义。 */
  onBack: () => void

  /** 旧页面的提交动作，保留全部校验、参数、上报与跳转。 */
  onSubmit: () => void

  /** 旧页面打开城市选择器的展示状态更新。 */
  onOpenCityPicker: () => void

  /** 旧页面的申请额度状态与对应 setter。 */
  applyLimit: string
  setApplyLimit: (value: string) => void

  /** 旧页面的姓名状态与对应 setter。 */
  setRealName: (value: string) => void

  /** 旧页面的身份证状态与对应 setter。 */
  setIdentity: (value: string) => void

  /** 旧页面的芝麻分状态与对应 setter。 */
  setSesameCredit: (value: string) => void

  /** 旧页面的房产资质状态与对应 setter。 */
  houseProperty: string
  setHouseProperty: (value: string) => void

  /** 旧页面的车产资质状态与对应 setter。 */
  carProperty: string
  setCarProperty: (value: string) => void

  /** 旧页面的公积金资质状态与对应 setter。 */
  housingFund: string
  setHousingFund: (value: string) => void

  /** 旧页面的社保资质状态与对应 setter。 */
  socialSecurity: string
  setSocialSecurity: (value: string) => void

  /** 旧页面的职业资质状态与对应 setter。 */
  job: string
  setJob: (value: string) => void

}

interface V2FormBusinessBridge {

  /** 保留旧页面的返回动作。 */
  handleBack: () => void

  /** 将新版资金需求选项写回旧 applyLimit 枚举。 */
  changeApplyLimit: (value: string) => void

  /** 将新版姓名输入写回旧表单状态。 */
  changeRealName: (value: string) => void

  /** 将新版身份证输入写回旧表单状态。 */
  changeIdentity: (value: string) => void

  /** 将新版芝麻分选项写回旧 sesameCredit 枚举。 */
  changeSesameCredit: (value: string) => void

  /** 切换房产资质对应的旧枚举值。 */
  toggleHouseProperty: () => void

  /** 切换车产资质对应的旧枚举值。 */
  toggleCarProperty: () => void

  /** 切换公积金资质对应的旧枚举值。 */
  toggleHousingFund: () => void

  /** 切换社保资质对应的旧枚举值。 */
  toggleSocialSecurity: () => void

  /** 切换营业执照职业资质对应的旧 job 枚举。 */
  toggleBusinessLicense: () => void

  /** 切换公务员等职业资质对应的旧 job 枚举。 */
  togglePublicSectorJob: () => void

  /** 参照 jurongyi-flow 唤起定位并打开城市选择器。 */
  openCityPicker: () => void

  /** 本地校验失败时保持旧表单提示文案。 */
  showInvalidIdentity: () => void

  /** 委托旧表单的完整提交链路。 */
  submit: () => void
}

/**
 * useV2FormBusinessBridge — 将 v2 表单用户意图桥接回 FillInformation 的既有状态与提交动作。
 * @returns {V2FormBusinessBridge} 供新表单皮肤注入的旧业务回调
 */
const useV2FormBusinessBridge = ({
  adChannelCode = '',
  applyLimit,
  carProperty,
  cityCodes = [],
  cityName = '',
  houseProperty,
  housingFund,
  job,
  onBack,
  onOpenCityPicker,
  onSubmit,
  setApplyLimit,
  setCarProperty,
  setCityCodes,
  setCityName,
  setHouseProperty,
  setHousingFund,
  setIdentity,
  setJob,
  setRealName,
  setSesameCredit,
  setSocialSecurity,
  socialSecurity,
}: Options): V2FormBusinessBridge => {
  const locationRequestedRef = useRef(false)
  const citySelectedManuallyRef = useRef(false)

  const { requestLocation } = useCityLocation({
    adChannelCode,
    cityOptions: cityDate,
  })

  const handleOpenCityPicker = useCallback(async() => {
    const validCodesCount = cityCodes.filter((item): item is string => Boolean(item)).length
    if ((cityName && validCodesCount) || locationRequestedRef.current) {
      onOpenCityPicker()
      return
    }

    locationRequestedRef.current = true
    const locatedCity = await requestLocation()
    if (!locatedCity || citySelectedManuallyRef.current) {
      return
    }

    if (setCityCodes && setCityName) {
      setCityCodes(locatedCity.codes)
      setCityName(locatedCity.name)
    }
  }, [cityCodes, cityName, onOpenCityPicker, requestLocation, setCityCodes, setCityName])

  return {
    handleBack: onBack,
    changeApplyLimit: setApplyLimit,
    changeRealName: setRealName,
    changeIdentity: setIdentity,
    changeSesameCredit: setSesameCredit,
    toggleHouseProperty: useCallback(() => setHouseProperty(houseProperty === '1' ? '0' : '1'), [houseProperty, setHouseProperty]),
    toggleCarProperty: useCallback(() => setCarProperty(carProperty === '1' ? '0' : '1'), [carProperty, setCarProperty]),
    toggleHousingFund: useCallback(() => setHousingFund(housingFund === '2' ? '0' : '2'), [housingFund, setHousingFund]),
    toggleSocialSecurity: useCallback(() => setSocialSecurity(socialSecurity === '2' ? '0' : '2'), [setSocialSecurity, socialSecurity]),
    toggleBusinessLicense: useCallback(() => setJob(job === '30' ? '10' : '30'), [job, setJob]),
    togglePublicSectorJob: useCallback(() => setJob(job === '40' ? '10' : '40'), [job, setJob]),
    openCityPicker: handleOpenCityPicker,
    showInvalidIdentity: () => Toast.show('请填写您的真实身份信息'),
    submit: onSubmit,
  }
}

export default useV2FormBusinessBridge
